import { defineStore } from 'pinia'
import { useNotification } from '@kyvg/vue3-notification'
import { ref, watch } from 'vue'
import { i18n } from '../i18n/index.js'
import { useRouter } from 'vue-router'
import { useAuthStore } from './auth.js'
import { useDigitalBankStore } from '../stores/digitalBank.js'
import { useTagFilterStore } from '@stores/filterTag.js'
import { useSharedFetcher } from '../services/sharedFetcher.js'
import {
  getContacts as getSharedContacts,
  getContactById as getSharedContactById,
  createContact as createSharedContact,
  updateContact as updateSharedContact,
  deleteContact as deleteSharedContact,
  restoreContact as restoreSharedContact
} from '@services/contacts'

export const useContactStore = defineStore('contactsStore', () => {
  const fetcher = useSharedFetcher()
  const authStore = useAuthStore()
  const expandNet = ref(authStore?.user?.progress?.expand_network)
  const $t = i18n.global.t
  const contacts = ref([])
  const contact = ref(null)
  const { notify } = useNotification()
  const searchQuery = ref('')
  const page = ref(1)
  const lastLoadPage = ref(0)
  let searchTimer = null
  const isSearch = ref(false)
  const count = ref(null)
  const router = useRouter()
  const isForks = ref(false)
  const digitalStore = useDigitalBankStore()
  const photo = ref('')
  const blob = ref('')
  const filterStore = useTagFilterStore()
  const getContacts = async (params) => {
    const isFork = params.isFork || isForks.value

    if (!isFork && contacts.value.length) return contacts.value

    let response
    try {
      response = await getSharedContacts(fetcher, {
        search_text: searchQuery.value || undefined,
        access_level: params.accessLevel,
        expertise_level: params.expertiseLevel,
        languages: params.languages,
        industries: params.industries,
        directions: params.directions,
        competences: params.competences,
        countries: params.countries,
        cities: params.cities,
        expand_network: Boolean(expandNet.value),
        take: params.take,
        page: page.value
      })
    } catch (e) {
      return [contacts.value, count.value]
    }

    const newContacts = response?.data
    count.value = response?.count

    if (page.value === 1) {
      contacts.value = newContacts
    } else {
      contacts.value = [...contacts.value, ...newContacts]
    }

    return [contacts.value, count.value]
  }

  const getContactById = async (params) => {
    try {
      const response = await getSharedContactById(fetcher, params.id, params.type)
      contact.value = response.data
      return contact.value
    } catch (e) {
      notify({
        title: $t('error_occurred'),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }
  }

  const loadNextPage = async () => {
    if (lastLoadPage.value && lastLoadPage.value > 1) {
      page.value = lastLoadPage.value || 1
    }
    page.value += 1
    lastLoadPage.value = page.value

    await getContacts({
      take: 24,
      isFork: true,
      accessLevel: filterStore?.accessLevel,
      expertiseLevel: filterStore?.expertiseLevel,
      languages: filterStore?.languages,
      industries: filterStore?.industries,
      directions: filterStore?.directions,
      competences: filterStore?.competences,
      countries: filterStore?.countries,
      cities: filterStore?.cities
    })
  }

  const addNewContact = async (dataInfo) => {
    const formData = new FormData()
    for (const key in dataInfo) {
      if (
        key !== 'avatar' &&
        key !== 'avatar_url' &&
        key !== 'country' &&
        isNullOrEmpty(dataInfo[key])
      ) {
        formData.append(key, dataInfo[key])
      }
    }

    // formData.append('isOwn', false)

    // if (cover) formData.append('cover', cover[0])
    if (blob.value) formData.append('avatar', blob.value)

    function isNullOrEmpty(value) {
      if (value === null) {
        return false
      }

      if (typeof value === 'string' && value.trim() === '') {
        return false
      }

      return !(typeof value === 'object' && Object.keys(value).length === 0)


    }

    let data
    try {
      data = await createSharedContact(fetcher, formData)
    } catch (e) {
      return 'error'
    }

    if (data?.success && blob.value) {
      notify({
        title: $t('new_contact'),
        type: 'success',
        ignoreDuplicates: true
      })
    }

    router.push({ name: 'digital_bank' })
  }

  const updateContact = async (contactId, dataInfo) => {
    const formData = new FormData()

    if (blob.value) {
      formData.append('avatar', blob.value)
    }

    for (const key in dataInfo) {
      // if (avatar) {
      //   if (key !== 'avatar_url' && key !== 'country' && dataInfo[key]) {
      //     formData.append(key, dataInfo[key])
      //   }
      // } else {
      if (key !== 'avatar_url' && key !== 'country' && dataInfo[key]) {
        formData.append(key, dataInfo[key])
      }
      // }
    }

    try {
      const data = await updateSharedContact(fetcher, contactId, formData)

      if (data?.data) {
        // Only a freshly-picked photo changes anything on the account itself.
        if (blob.value) {
          await authStore.current()
        }

        // `blob` is set only when a NEW photo was picked this session, so it
        // says nothing about whether the contact already has one. Keying the
        // warning off it meant every ordinary edit — of a contact with a
        // perfectly good photo — got told to add a photo, and never got a
        // confirmation that the save had worked. Ask the saved record instead.
        const hasPhoto = !!blob.value || !!data.data.avatar_url

        notify(
          hasPhoto
            ? { title: $t('edit_contact_bank'), type: 'success', ignoreDuplicates: true }
            : { title: $t('add_photo_warning'), type: 'warn', ignoreDuplicates: true }
        )
      }
    } finally {
      await digitalStore.getContactsBank(true, true, 24)
    }
  }

  const restoreContact = async (params) => {
    try {
      await restoreSharedContact(fetcher, params)
    } catch (e) {
      // Pre-existing behavior: this call never surfaced errors to the UI.
    }
  }

  const deleteContact = async (id) => {
    try {
      return await deleteSharedContact(fetcher, id)
    } catch (e) {
      notify({
        title: $t('error_occurred'),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }
  }

  watch(
    searchQuery,
    () => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        page.value = 1

        if (isSearch.value) {
          getContacts({ take: 24, isFork: true })
        }
      }, 1000)
    },
    { deep: true }
  )

  return {
    getContacts,
    getContactById,
    loadNextPage,
    contacts,
    searchQuery,
    page,
    isSearch,
    count,
    contact,
    isForks,
    lastLoadPage,
    // filteredContacts,
    // getFilteredContact,
    addNewContact,
    updateContact,
    restoreContact,
    deleteContact,
    photo,
    blob
  }
})
